import * as React from "react";
import { FunctionComponent } from "react";
import { countryList } from "../../../ts/constants/form-values";

// Die Leitweg-ID ist ein rein deutsches Konstrukt (E-Rechnung an öffentliche Auftraggeber)
// und daher nur für Adressen mit dem Land „Deutschland" relevant.
const GERMAN_COUNTRY_CODE = "DE";

export interface IAddressVariant {
	isPublicAuthority: boolean;
	isGermanAddress: boolean;
	showLeitwegIdField: boolean;
}

interface IVariantOptions {
	/** Feldnamen-Präfix: `""` im Kundenpfad, `"billing."` in der Gastbestellung. */
	namePrefix?: string;
	/** Bestehende Adresse: die Variante ist festgeschrieben und nicht umschaltbar. */
	isEdit?: boolean;
	/** Leitweg-ID der bearbeiteten Adresse — im `isEdit`-Fall bestimmt sie die Variante. */
	existingLeitwegId?: string;
	/** Land der bearbeiteten Adresse, solange das Feld selbst noch keinen Wert liefert. */
	fallbackCountry?: string;
}

/** Liest einen ggf. verschachtelten Fehler (`errors.billing.leitwegId`) über seinen Pfad. */
export function fieldError(errors: any, path: string): any {
	return path.split(".").reduce((current: any, segment: string) => current?.[segment], errors);
}

/**
 * Leitet die Adressart aus dem Formularzustand ab. Bewusst eine gewöhnliche Funktion und kein
 * Hook mit `useFormContext`: der Submit-Handler des Formulars braucht dieselben Flags, und der
 * liegt außerhalb eines möglichen Providers.
 */
export function deriveAddressVariant(watch: any, options: IVariantOptions): IAddressVariant {
	const prefix = options.namePrefix ?? "";

	const isPublicAuthority = options.isEdit
		? !!options.existingLeitwegId
		: watch(`${prefix}isPublicAuthority`) === "true";
	const selectedCountry =
		watch(`${prefix}country`) ?? options.fallbackCountry ?? countryList[0].value;
	// Groß-/Kleinschreibung wie serverseitig ignorieren (AddressApiModel.IsGermanAddress),
	// damit Feld-Sichtbarkeit und Pflichtfeld-Validierung nie auseinanderlaufen.
	const isGermanAddress = selectedCountry?.toUpperCase() === GERMAN_COUNTRY_CODE;

	return {
		isPublicAuthority,
		isGermanAddress,
		showLeitwegIdField: isPublicAuthority && isGermanAddress
	};
}

/**
 * Submit-Regel der Adressart, gemeinsam für Kundenpfad und Gastbestellung: `isPublicAuthority`
 * geht explizit mit, eine Leitweg-ID nur dann, wenn das Feld auch sichtbar war. Bei
 * KuP-verwalteten Adressen bleibt der gespeicherte Wert stehen (das Feld ist dort `readOnly`).
 *
 * `target` ist das Objekt, das gesendet wird — in der Gastbestellung also `data.billing`.
 */
export function normalizeAddressVariant(
	target: Record<string, any>,
	variant: IAddressVariant,
	options: { isKupManaged?: boolean; existingLeitwegId?: string }
): void {
	target.isPublicAuthority = variant.isPublicAuthority;
	if (!variant.showLeitwegIdField) {
		target.leitwegId = "";
	} else if (options.isKupManaged) {
		target.leitwegId = options.existingLeitwegId;
	}
}

/**
 * Radio-Gruppe „Adressart". Getrennt von <see cref="LeitwegIdField"/>, weil die beiden im
 * Formular nicht benachbart stehen — die Platzierung bestimmt das jeweilige Formular.
 */
export const AddressVariantRadios: FunctionComponent<{
	register: any;
	namePrefix?: string;
	isEdit?: boolean;
	/** Unterscheidet die Element-`id`s, falls mehrere Instanzen auf einer Seite liegen. */
	idScope?: string;
}> = (props) => {
	const prefix = props.namePrefix ?? "";
	// Ohne idScope bleiben die ids des Bestands unverändert, damit das gerenderte DOM im
	// Kundenpfad identisch bleibt. Eine zweite Instanz auf derselben Seite muss einen Scope
	// setzen, sonst kollidieren ids und aria-labelledby zeigt auf ein fremdes Label.
	const idSuffix = props.idScope ? `_${props.idScope}` : "";
	const labelId = `address_variant_label${idSuffix}`;
	const businessId = `address_variant_business${idSuffix}`;
	const publicId = `address_variant_public${idSuffix}`;

	return (
		<div className="form-group row" role="radiogroup" aria-labelledby={labelId}>
			<label id={labelId} className="col-sm-4 col-form-label">
				Adressart
			</label>
			<div className="col-sm-8">
				<div className="radio-item">
					<input
						type="radio"
						id={businessId}
						name={`${prefix}isPublicAuthority`}
						value="false"
						disabled={props.isEdit}
						ref={props.register()}
					/>
					<label htmlFor={businessId}>Geschäftlich</label>
				</div>
				<div className="radio-item">
					<input
						type="radio"
						id={publicId}
						name={`${prefix}isPublicAuthority`}
						value="true"
						disabled={props.isEdit}
						ref={props.register()}
					/>
					<label htmlFor={publicId}>Öffentlicher Auftraggeber</label>
				</div>
			</div>
		</div>
	);
};

/**
 * Leitweg-ID-Feld, sichtbar nur bei „Öffentlicher Auftraggeber" und Land Deutschland. Gemeinsam
 * genutzt, damit es nicht wie in ECOM-3665 in einem der Adressformulare fehlt.
 */
export const LeitwegIdField: FunctionComponent<{
	register: any;
	errors: any;
	variant: IAddressVariant;
	namePrefix?: string;
	/** Leitweg-ID kommt aus KuP und ist nicht editierbar. */
	isKupManaged?: boolean;
	/** Unterscheidet die Feld-`id`, falls mehrere Instanzen auf einer Seite liegen. */
	idScope?: string;
	/** Rendert einen Serverfehler zum Feld. Kommt vom Formular, weil dort das Markup festliegt. */
	createApiError: (key: string) => any;
}> = (props) => {
	const prefix = props.namePrefix ?? "";
	const fieldId = `leitwegid_input_${props.idScope ?? "billing"}`;
	const error = fieldError(props.errors, `${prefix}leitwegId`);
	const apiError = props.createApiError("LeitwegId");

	if (!props.variant.showLeitwegIdField) {
		// Serverseitiger Leitweg-ID-Fehler bei ausgeblendetem Feld: sonst wäre die Meldung
		// unsichtbar und das Formular ließe sich nicht mehr abschicken.
		return apiError ? (
			<div className="form-group row">
				<div className="col-sm-4 col-form-label">Leitweg-ID</div>
				<div className="col-sm-8">{apiError}</div>
			</div>
		) : null;
	}

	return (
		<div className="form-group row">
			<label htmlFor={fieldId} className="col-sm-4 col-form-label">
				Leitweg-ID {!props.isKupManaged && <span className="mandatory">*</span>}
			</label>
			<div className="col-sm-8">
				<input
					type="text"
					id={fieldId}
					name={`${prefix}leitwegId`}
					className="form-control"
					placeholder="Leitweg-ID"
					aria-required={props.isKupManaged ? "false" : "true"}
					aria-invalid={error ? "true" : "false"}
					readOnly={props.isKupManaged}
					maxLength={46}
					ref={props.register({ required: !props.isKupManaged })}
				/>
				{!props.isKupManaged && error && (
					<span role="alert" className="form-error">
						Bitte geben Sie die Leitweg-ID ein.
					</span>
				)}
				{apiError}
			</div>
		</div>
	);
};
